import { IssuerCommunicationBulkReportConfig } from '@/modules/issuer-dashboard/components/communications/IssuerCommunicationBulkReportConfig.tsx';
import { CommunicationFormValues } from '@/modules/issuer-dashboard/helpers/buildCommunicationPayload.ts';
import { CalendarInputProps, CustomCheckbox, SelectPaginate, SelectPaginateOption } from 'ui';
import { BulkReportType, ReportFormat } from 'api';

interface IssuerCommunicationGeneratedAttachmentsConfigProps {
  values: CommunicationFormValues;
  registerId: number;
  selectedBulkReportClasses: SelectPaginateOption<number>[];
  selectedBulkCertificateClasses: SelectPaginateOption<number>[];
  selectedDividendEvent: SelectPaginateOption<number> | null;
  generatedAttachmentsError?: string | null;
  onToggleGenerateReport: (checked: boolean) => void;
  onToggleGenerateCertificate: (checked: boolean) => void;
  onChangeReportType: (value: BulkReportType) => void;
  onChangeReportClasses: (items: SelectPaginateOption<number>[]) => void;
  onChangeReportDateRange: CalendarInputProps['rangeOnChange'];
  onChangeReportEndDate: CalendarInputProps['onChange'];
  onChangeReportFinancialYear: (value: number | null) => void;
  onChangeReportDividendEvent: (value: SelectPaginateOption<number> | null) => void;
  onChangeReportFileType: (value: ReportFormat) => void;
  onChangeCertificateClasses: (items: SelectPaginateOption<number>[]) => void;
  onAsyncClasses: (page: number, search?: string) => Promise<SelectPaginateOption<number>[]>;
}

export const IssuerCommunicationGeneratedAttachmentsConfig = ({
  values,
  registerId,
  selectedBulkReportClasses,
  selectedBulkCertificateClasses,
  selectedDividendEvent,
  generatedAttachmentsError,
  onToggleGenerateReport,
  onToggleGenerateCertificate,
  onChangeReportType,
  onChangeReportClasses,
  onChangeReportDateRange,
  onChangeReportEndDate,
  onChangeReportFinancialYear,
  onChangeReportDividendEvent,
  onChangeReportFileType,
  onChangeCertificateClasses,
  onAsyncClasses,
}: IssuerCommunicationGeneratedAttachmentsConfigProps) => {
  return (
    <div className="space-y-4 border border-gray-200 p-4">
      <div className="space-y-2">
        <label className="flex items-center gap-2">
          <CustomCheckbox
            checked={values.generate_report}
            onChange={event => onToggleGenerateReport(event.target.checked)}
            className="select-none mr-0 leading-none"
          />
          <span className="text-sm leading-5 text-gray-900">
            Generate & attach individual report
          </span>
        </label>
        {values.generate_report && (
          <IssuerCommunicationBulkReportConfig
            values={values}
            registerId={registerId}
            selectedBulkReportClasses={selectedBulkReportClasses}
            selectedDividendEvent={selectedDividendEvent}
            onChangeReportType={onChangeReportType}
            onChangeClasses={onChangeReportClasses}
            onChangeDateRange={onChangeReportDateRange}
            onChangeEndDate={onChangeReportEndDate}
            onChangeFinancialYear={onChangeReportFinancialYear}
            onChangeDividendEvent={onChangeReportDividendEvent}
            onChangeFileType={onChangeReportFileType}
            onAsyncClasses={onAsyncClasses}
          />
        )}
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2">
          <CustomCheckbox
            checked={values.generate_certificate}
            onChange={event => onToggleGenerateCertificate(event.target.checked)}
            className="select-none mr-0 leading-none"
          />
          <span className="text-sm leading-5 text-gray-900">Attach individual certificate</span>
        </label>
        {values.generate_certificate && (
          <div className="space-y-4">
            <SelectPaginate<number, true>
              isMulti
              label="Certificate class"
              cacheUniqs={[registerId]}
              debounceTimeoutSearch={700}
              onAsync={onAsyncClasses}
              value={selectedBulkCertificateClasses}
              onChange={onChangeCertificateClasses}
              className="z-[3]"
            />
          </div>
        )}
      </div>

      {generatedAttachmentsError && (
        <div className="text-sm text-red-600">{generatedAttachmentsError}</div>
      )}
    </div>
  );
};
